import {
  Alert,
  Button,
  Dialog,
  LinkButton,
  readJson,
  TextArea,
  useActionKey,
} from "@emdur/ui";
import { useEffect, useRef, useState } from "react";
import { useApi } from "../lib/apiContext";
import {
  consumeApprovalVerification,
  hasApprovalVerification,
  startApprovalVerification,
} from "../lib/auth";
import { errorText } from "../lib/errors";
import { clientReasons } from "../lib/clients";
import {
  type Proposal,
  proposalDiff,
  proposalExpiry,
  proposalHref,
  proposalNeedsVerification,
  proposalPath,
  proposalSentence,
} from "../lib/proposals";
import { PermissionDiff } from "./Permissions";
import { ECompletes } from "./ECompletes";
import { showProposalDecision } from "./ProposalDecisionNotice";
import { useRegistry } from "./RegistryProvider";
import { PermissionRefreshAction } from "./PermissionRefreshAction";

export function ProposalReview({
  proposal,
  onDecided,
  onConfirmed,
  command,
  autoVerify = false,
}: {
  proposal: Proposal;
  onDecided: (proposal: Proposal) => void;
  onConfirmed?: (proposal: Proposal) => void;
  command?: { action: "approve" | "decline"; sequence: number };
  autoVerify?: boolean;
}) {
  const api = useApi();
  const registry = useRegistry();
  const keys = useActionKey();
  const catalogue = registry.catalogues[proposal.organization_id] ?? [];
  const agent = registry.agents[proposal.agent_id];
  const app = registry.apps.find(
    (app) => app.id === proposal.arguments.path?.client_id,
  );
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [completed, setCompleted] = useState<Proposal | null>(null);
  const attempted = useRef(false);
  const expired = Date.parse(proposal.expires_at) <= Date.now();
  async function decide(outcome: "accept" | "reject", automatic = false) {
    if (
      keys.sending.current ||
      completed ||
      proposal.state !== "pending" ||
      expired
    )
      return;
    keys.sending.current = true;
    setBusy(true);
    setError("");
    try {
      const latest = await readJson<Proposal>(
        api.fetch,
        proposalPath(proposal.organization_id, proposal.id),
      );
      if (!latest.etag)
        throw new Error(
          "The proposal's version could not be read. Reload before deciding.",
        );
      if (
        latest.data.arguments_hash !== proposal.arguments_hash ||
        latest.data.state !== "pending"
      )
        throw new Error(
          "This proposal changed elsewhere. Reload it and review the change again.",
        );
      if (
        outcome === "accept" &&
        proposalNeedsVerification(latest.data, catalogue)
      ) {
        const intent = {
          org: proposal.organization_id,
          proposal: proposal.id,
          etag: latest.etag,
          argumentsHash: latest.data.arguments_hash,
          personId: registry.personId,
          expiresAt: Date.now() + 300000,
        };
        if (!registry.personId)
          throw new Error(
            "Your identity could not be checked. Reload and try again.",
          );
        if (!consumeApprovalVerification(intent)) {
          if (automatic)
            throw new Error(
              "The proposal changed while you signed in. Review it before approving again.",
            );
          await startApprovalVerification(intent);
          return;
        }
      }
      const body =
        outcome === "reject" ? { outcome, reason: reason.trim() } : { outcome };
      const key = keys.keyFor(`decision:${proposal.id}`, body);
      const answer = await readJson<Proposal>(
        api.fetch,
        `${proposalPath(proposal.organization_id, proposal.id)}/decision`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "If-Match": latest.etag,
            "Idempotency-Key": key,
          },
          body: JSON.stringify(body),
        },
      );
      keys.spent(`decision:${proposal.id}`, key);
      setDeclining(false);
      if (
        outcome === "accept" &&
        ["accepted", "executed"].includes(answer.data.state)
      ) {
        onConfirmed?.(proposal);
        setCompleted(answer.data);
      } else {
        showProposalDecision(answer.data);
        onDecided(answer.data);
      }
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      keys.sending.current = false;
      setBusy(false);
    }
  }
  useEffect(() => {
    if (completed || busy || expired || proposal.state !== "pending") return;
    if (command?.action === "approve") void decide("accept");
    if (command?.action === "decline") setDeclining(true);
  }, [command?.sequence]);
  useEffect(() => {
    if (
      autoVerify &&
      !registry.loading &&
      registry.personId &&
      !attempted.current &&
      hasApprovalVerification(proposal.organization_id, proposal.id)
    ) {
      attempted.current = true;
      void decide("accept", true);
    }
  }, [autoVerify, registry.loading, registry.personId, proposal.id]);
  const sponsors =
    agent?.sponsors
      .map(
        (person) =>
          registry.sponsors[person] ?? "A sponsor whose name is unavailable",
      )
      .join(", ") ?? "Sponsor information unavailable";
  const reasons = clientReasons(proposal.arguments.body?.["x-emdur-reasons"]);
  return (
    <>
      <section
        aria-labelledby={`sentence-${proposal.id}`}
        className="min-w-0 border-b border-line py-6"
      >
        <h2 id={`sentence-${proposal.id}`} className="decision-sentence">
          {proposalSentence(proposal, catalogue, app?.client_name)}
        </h2>
        <p className="my-4 flex flex-wrap gap-x-1 text-sm text-muted">
          <span className="font-strong text-ink">
            {agent?.name ?? "Agent information unavailable"}
          </span>
          <span>· sponsored by {sponsors} ·</span>
          <time
            dateTime={proposal.expires_at}
            title={new Date(proposal.expires_at).toLocaleString("en", {
              dateStyle: "full",
              timeStyle: "short",
            })}
          >
            {expired ? "expired" : proposalExpiry(proposal.expires_at)}
          </time>
        </p>
        <div className="flex flex-col gap-5">
          {proposal.justification &&
            !/^Review\b/i.test(proposal.justification) && (
              <p className="text-sm text-muted">{proposal.justification}</p>
            )}
          {proposal.operation_id === "updateClient" ? (
            <PermissionDiff
              diff={proposalDiff(proposal)}
              catalogue={catalogue}
              reasons={reasons}
              removedReasons={app?.["x-emdur-reasons"]}
              showTitles={false}
            />
          ) : (
            <p className="text-sm">
              Review the operation and its evidence before deciding.
            </p>
          )}
          {proposal.mode === "suggest" && (
            <p className="text-sm text-muted">
              A suggestion: approving records your decision without applying the
              change.
            </p>
          )}
          {completed && proposal.state === "pending" ? (
            <ECompletes
              onComplete={() => {
                showProposalDecision(completed);
                onDecided(completed);
              }}
            />
          ) : proposal.state === "pending" ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                isLoading={busy}
                isDisabled={expired || !!registry.error}
                onPress={() => void decide("accept")}
              >
                Approve
              </Button>
              <Button
                isDisabled={busy || expired}
                onPress={() => setDeclining(true)}
              >
                Decline
              </Button>
              <LinkButton
                variant="quiet"
                href={proposalHref(proposal.organization_id, proposal.id)}
              >
                Open proposal
              </LinkButton>
            </div>
          ) : (
            <p role="status">
              Decision: {proposal.state}
              {proposal.decision?.reason
                ? ` · ${proposal.decision.reason}`
                : ""}
            </p>
          )}
          {error && !declining && (
            <Alert tone="danger">
              {error}
              <div className="mt-2">
                <Button onPress={() => void registry.reload()}>
                  Reload proposals
                </Button>
              </div>
            </Alert>
          )}
        </div>
      </section>
      <Dialog
        title="Decline this proposal?"
        isOpen={declining}
        onOpenChange={(open) => !open && !busy && setDeclining(false)}
      >
        <div className="flex flex-col gap-4">
          <TextArea
            label="Reason for declining"
            value={reason}
            onChange={setReason}
            maxLength={500}
            isRequired
            hint="Tell the agent's sponsor what needs to change."
          />
          {error && (
            <Alert tone="danger">
              {error}
              <PermissionRefreshAction when={error} />
            </Alert>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="danger"
              isDisabled={!reason.trim()}
              isLoading={busy}
              onPress={() => void decide("reject")}
            >
              Decline proposal
            </Button>
            <Button isDisabled={busy} onPress={() => setDeclining(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
